import React from 'react'
import { ComponentProps } from 'react'

import { DetailsItemsList, Stack } from '@factorialco/f0-react/dist/experimental'
import { DealDetailFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { contactFullName } from 'modules/finance/lib/contactsUtils'

interface DealDetailsInfoProps {
  deal: DealDetailFragment
}

export const DealDetailsInfo: React.FC<DealDetailsInfoProps> = ({ deal }) => {
  if (!deal) return null

  const { navigation } = useNavigation()

  const detailsSection: ComponentProps<typeof DetailsItemsList>['details'] = [
    {
      title: i18n.t('finance.deals.details.creator'),
      content: deal.author
        ? {
            type: 'person',
            firstName: deal.author.firstName || '',
            lastName: deal.author.lastName || '',
            avatarUrl: deal.author.avatar?.url || undefined,
          }
        : {
            type: 'item',
            text: '-',
          },
    },
    {
      title: i18n.t('finance.deals.details.owner'),
      content: deal.owner
        ? {
            type: 'person',
            firstName: deal.owner.firstName || '',
            lastName: deal.owner.lastName || '',
            avatarUrl: deal.owner.avatar?.url || undefined,
          }
        : {
            type: 'item',
            text: '-',
          },
    },
  ]

  const clientDetails: ComponentProps<typeof DetailsItemsList>['details'] = []

  if (deal.client) {
    clientDetails.push({
      title: i18n.t('finance.deals.details.client_details.legal_name'),
      content: {
        type: 'item',
        text: contactFullName(deal.client) || '-',
        action: {
          type: 'navigate',
          href: navigation.finance.sales.clients.contactId.fullPath({ contactId: deal.client.id }),
        },
      },
    })

    if (deal.client.email) {
      clientDetails.push({
        title: i18n.t('finance.deals.details.client_details.email'),
        content: {
          type: 'item',
          text: deal.client.email,
          action: {
            type: 'copy',
          },
        },
      })
    }

    if (deal.client.phoneNumber) {
      clientDetails.push({
        title: i18n.t('finance.deals.details.client_details.phone'),
        content: {
          type: 'item',
          text: deal.client.phoneNumber,
          action: {
            type: 'copy',
          },
        },
      })
    }

    if (deal.client.website) {
      clientDetails.push({
        title: i18n.t('finance.deals.details.client_details.website'),
        content: {
          type: 'item',
          text: deal.client.website,
          action: {
            type: 'copy',
          },
        },
      })
    }
  }

  return (
    <Stack gap="8">
      <DetailsItemsList
        title={i18n.t('finance.deals.details.details_section')}
        details={detailsSection}
      />
      {deal.client && clientDetails.length > 0 && (
        <DetailsItemsList
          title={i18n.t('finance.deals.details.client_section')}
          details={clientDetails}
        />
      )}
    </Stack>
  )
}
